import React, { useContext, useState } from "react";

import { AuthContext, UnitContext } from "./contextExporter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qoMlParts } from "../utils/queryOptions";
import { useSocketEmit } from "../hooks/useSocketEmit";
import { useSocketListener } from "../hooks/useSocketListener";
import { toast } from "react-toastify";
import { IMccUnit, IUnitPin, ITodaysUnit } from "../types/unitTypes";
import { useNavigate } from "react-router";
import {
  ILiveLogUpdate,
  IOfflineLogUpdate,
  updateUnitLiveLogFn,
  updateUnitOfflineLogFn,
} from "../api/unitLog";

type TSetPinWithEmit = (
  newValue: React.SetStateAction<IUnitPin>,
  preventEmit?: boolean
) => void;

export function UnitContextProvider({
  children,
  unit,
  liveDetails,
  isLive,
}: {
  children: React.ReactNode;
  unit: IMccUnit;
  liveDetails: ITodaysUnit | undefined;
  isLive: boolean;
}) {
  // canlı dersse ortak logdan, değilse kişisel logdan kaldığı yere baksın
  const initialPin = isLive ? liveDetails?.live_logs : unit.offline_logs;
  const { user, isInstructor, isFulltimeSelfUser } = useContext(AuthContext);
  const [pin, setPin] = useState<IUnitPin>(
    initialPin || {
      mlOrder: 0,
      partOrder: 0,
      partMode: isFulltimeSelfUser ? "VIDEO" : "ZOOM",
    }
  );

  // kendi hızında ilerleyen öğrenci dersin son bölümünü de bitirdi mi
  const [isUnitFinished, setIsUnitFinished] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /*  */

  const updateMutationLive = useMutation({
    mutationFn: (data: ILiveLogUpdate) =>
      updateUnitLiveLogFn(liveDetails!.unit_live_log_id, data),
  });

  const updateMutationOffline = useMutation({
    mutationFn: (data: IOfflineLogUpdate) => updateUnitOfflineLogFn(data),
  });

  /*  */

  const setPinWithEmit: TSetPinWithEmit = (newValue, preventEmit = false) => {
    setIsUnitFinished(false);
    setPin((prev) => {
      const newPin =
        typeof newValue === "function"
          ? (newValue as (prev: IUnitPin) => IUnitPin)(prev)
          : newValue;

      if (isInstructor && isLive) {
        if (!preventEmit) {
          if (confirm("Dersi yönlendirmek istediğine emin misin?")) {
            emit("unitPinChangeCall", {
              cohorts: liveDetails?.cohorts || [],
              newPin,
            });
          }
        }
        updateMutationLive.mutate({
          instructor_id: user!.id,
          details: newPin,
        });
      }

      if (!isLive) {
        updateMutationOffline.mutate({
          unit_id: unit.unit_id,
          user_id: user!.id,
          details: newPin,
        });
      }

      return newPin;
    });
  };

  const { emit } = useSocketEmit();

  const currentMl = unit && pin && unit.unit_mls[pin.mlOrder];
  const nextMl = (unit && pin && unit.unit_mls[pin.mlOrder + 1]) || undefined;

  const { data: currentMlParts, isLoading: arePartsLoading } = useQuery(
    qoMlParts({
      ml_id: currentMl?.ml_id,
      add_outro: !isFulltimeSelfUser,
    })
  );

  const currentPart =
    currentMlParts && pin ? currentMlParts[pin?.partOrder] : null;

  function next(args?: { preventEmit?: boolean }) {
    // ml aynı, part aynı, partMode değişir

    // burada yeni pin için bir kopya oluşturalım
    const newPin: IUnitPin = { ...pin };

    // Bir part'ın videosu (Anlatım) bittiğinde HER ZAMAN o part'ın Uygulama
    // (PRACTICE) kısmına geç — son part ya da son ML olsa bile. Aksi halde
    // ML'in son part'ında video bitince Uygulama atlanıp doğrudan sonraki
    // ML'e (veya derste sona) geçiliyordu. Değerlendirme/outro part'ında
    // id === -1 olur, orada uygulama yok; o yüzden onu hariç tutuyoruz.
    if (newPin.partMode === "VIDEO" && currentPart && currentPart.id > -1) {
      newPin.partMode = "PRACTICE";
      setPinWithEmit(newPin, args?.preventEmit);
      return;
    }

    if (
      currentMlParts &&
      newPin.mlOrder === unit.unit_mls.length - 1 &&
      newPin.partOrder === currentMlParts.length - 1
    ) {
      // hem son ml hem değerlendirme: canlı derste do nothing, kendi hızında
      // ilerleyende "ders bitti" ekranı (yoksa öğrenci son soruda takılı kalıyordu)
      if (!isLive) setIsUnitFinished(true);
      return;
    } else if (
      !currentMlParts ||
      newPin.partOrder === currentMlParts.length - 1
    ) {
      // burada ml değişiyor: ya girişteyiz ya da aralardaki ml'in değerlendirmesi
      newPin.mlOrder = pin.mlOrder + 1;
      newPin.partOrder = 0;
      newPin.partMode = (isLive && nextMl.is_zoom) ? "ZOOM" : "VIDEO";
    } else {
      if (newPin.partMode !== "PRACTICE") {
        newPin.partMode = "PRACTICE";
      } else {
        // burada değerlendirmeye geçme ihtimali var
        newPin.partMode =
          isLive && (currentMl.is_zoom || pin.partOrder + 2 === currentMlParts.length)
            ? "ZOOM"
            : "VIDEO";
        newPin.partOrder = pin.partOrder + 1;
      }
    }

    setPinWithEmit(newPin, args?.preventEmit);
  }

  useSocketListener<IUnitPin>("unitPinChangeReply", (data) => {
    if (!isInstructor) {
      toast.info("Eğitmen seni başka bir bölüme yönlendiriyor, lütfen bekle.");
      setTimeout(() => {
        setPinWithEmit(data);
      }, 2000);
    }
  });

  useSocketListener<IUnitPin>("unitFinishReply", () => {
    toast.info("Ders tamamlandı. Ana sayfaya yönlendiriyorum.");
    queryClient.invalidateQueries({
      queryKey: ["todaysUnit"],
    });
    setTimeout(() => {
      navigate("/");
    }, 2000);
  });

  return (
    <UnitContext.Provider
      value={{
        pin,
        setPinWithEmit,
        canNavigate: isInstructor || !isLive,
        isLive,
        unit,
        currentMl,
        currentMlParts: currentMlParts || null,
        currentPart,
        next,
        arePartsLoading,
        isUnitFinished,
      }}
    >
      {children}
    </UnitContext.Provider>
  );
}
